import { useCallback, useEffect, useReducer, useRef } from 'react';

export interface PatientOpened {
  patientId: string;
  fullName: string | null;
}

export type PatientSyncState = 'loading' | 'synced' | 'out-of-sync' | 'sync-stopped';

export interface PatientSyncLocation {
  isConsultationActive: boolean;
  isOnOverview: boolean;
  shownPatientId: string | null;
}

interface MachineState {
  activePatient: PatientOpened | null;
  pendingPatient: PatientOpened | null;
  lastReceivedPatient: PatientOpened | null;
  syncStopped: boolean;
  loading: boolean;
  isConsultationActive: boolean;
  shownPatientId: string | null;
}

type MachineAction =
  | { type: 'patient-opened'; patient: PatientOpened }
  | { type: 'loading-finished' }
  | { type: 'pending-patient-applied' }
  | { type: 'sync-stopped' }
  | { type: 'sync-started' }
  | { type: 'manual-switch'; patientId: string }
  | { type: 'location-changed'; location: PatientSyncLocation }
  | { type: 'reset' };

const initialMachineState: MachineState = {
  activePatient: null,
  pendingPatient: null,
  lastReceivedPatient: null,
  syncStopped: false,
  loading: true,
  isConsultationActive: false,
  shownPatientId: null,
};

function followPendingPatient(state: MachineState): MachineState {
  if (state.syncStopped || !state.pendingPatient) return state;
  return { ...state, activePatient: state.pendingPatient, pendingPatient: null };
}

function rememberReceivedPatient(state: MachineState, patient: PatientOpened): MachineState {
  return state.lastReceivedPatient?.patientId === patient.patientId
    ? state
    : { ...state, lastReceivedPatient: patient };
}

function followPatientOpened(state: MachineState, patient: PatientOpened): MachineState {
  if (patient.patientId === state.activePatient?.patientId) {
    return state.pendingPatient ? { ...state, pendingPatient: null } : state;
  }
  if (patient.patientId === state.shownPatientId) {
    return { ...state, activePatient: patient, pendingPatient: null };
  }
  if (patient.patientId === state.pendingPatient?.patientId) return state;
  if (state.isConsultationActive || state.syncStopped || state.pendingPatient) {
    return { ...state, pendingPatient: patient };
  }
  return { ...state, activePatient: patient, pendingPatient: null };
}

function followLastReceived(state: MachineState): MachineState {
  const lastReceived = state.lastReceivedPatient;
  if (state.syncStopped) return state;
  if (state.activePatient?.patientId === lastReceived?.patientId && !state.pendingPatient) {
    return state;
  }
  return { ...state, activePatient: lastReceived, pendingPatient: null };
}

function liftStop(state: MachineState): MachineState {
  const lastReceived = state.lastReceivedPatient;
  if (!lastReceived) return { ...state, syncStopped: false };
  if (state.isConsultationActive && state.shownPatientId !== lastReceived.patientId) {
    return { ...state, syncStopped: false, pendingPatient: { ...lastReceived } };
  }
  return { ...state, syncStopped: false, activePatient: { ...lastReceived }, pendingPatient: null };
}

function changeLocation(state: MachineState, location: PatientSyncLocation): MachineState {
  const moved =
    state.isConsultationActive !== location.isConsultationActive ||
    state.shownPatientId !== location.shownPatientId;
  const next = moved
    ? {
        ...state,
        isConsultationActive: location.isConsultationActive,
        shownPatientId: location.shownPatientId,
      }
    : state;
  if (location.shownPatientId === next.pendingPatient?.patientId) return followPendingPatient(next);
  const leftPatientOverview = !state.isConsultationActive && state.shownPatientId !== null;
  const leftActivePatient = state.shownPatientId === state.activePatient?.patientId;
  if (location.isOnOverview && (leftPatientOverview || leftActivePatient))
    return { ...next, activePatient: null, pendingPatient: next.lastReceivedPatient };
  return next;
}

export function patientSyncReducer(state: MachineState, action: MachineAction): MachineState {
  switch (action.type) {
    case 'patient-opened': {
      const loaded = state.loading ? { ...state, loading: false } : state;
      return rememberReceivedPatient(followPatientOpened(loaded, action.patient), action.patient);
    }
    case 'loading-finished':
      return state.loading ? { ...state, loading: false } : state;
    case 'pending-patient-applied':
      return followPendingPatient(state);
    case 'sync-stopped':
      return state.syncStopped ? state : { ...state, syncStopped: true };
    case 'sync-started':
      return state.syncStopped ? liftStop(state) : state;
    case 'manual-switch':
      if (!state.lastReceivedPatient) return state;
      if (action.patientId === state.lastReceivedPatient.patientId)
        return followLastReceived(state);
      return patientSyncReducer(state, { type: 'sync-stopped' });
    case 'location-changed':
      return changeLocation(state, action.location);
    case 'reset':
      return {
        ...initialMachineState,
        isConsultationActive: state.isConsultationActive,
        shownPatientId: state.shownPatientId,
      };
    default:
      return state;
  }
}

export interface PatientSyncMachine {
  syncState: PatientSyncState;
  activePatient: PatientOpened | null;
  pendingPatient: PatientOpened | null;
  finishLoading: () => void;
  applyPendingPatient: () => void;
  stopSync: () => void;
  startSync: () => void;
  reportManualSwitch: (patientId: string) => void;
  reportLocation: (location: PatientSyncLocation) => void;
}

export function usePatientSyncMachine(
  patientOpened: PatientOpened | null,
  sessionKey: string | number | null,
  isConnected: boolean
): PatientSyncMachine {
  const isConnectedRef = useRef(isConnected);
  isConnectedRef.current = isConnected;

  const [state, dispatch] = useReducer(patientSyncReducer, initialMachineState);

  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [sessionKey]);

  useEffect(() => {
    if (!isConnectedRef.current || !patientOpened) return;
    dispatch({ type: 'patient-opened', patient: patientOpened });
  }, [patientOpened]);

  const finishLoading = useCallback(() => dispatch({ type: 'loading-finished' }), []);
  const applyPendingPatient = useCallback(() => dispatch({ type: 'pending-patient-applied' }), []);
  const stopSync = useCallback(() => dispatch({ type: 'sync-stopped' }), []);
  const startSync = useCallback(() => dispatch({ type: 'sync-started' }), []);
  const reportManualSwitch = useCallback(
    (patientId: string) => dispatch({ type: 'manual-switch', patientId }),
    []
  );
  const reportLocation = useCallback(
    (location: PatientSyncLocation) => dispatch({ type: 'location-changed', location }),
    []
  );

  const syncState: PatientSyncState = state.syncStopped
    ? 'sync-stopped'
    : state.loading && isConnected
      ? 'loading'
      : state.pendingPatient
        ? 'out-of-sync'
        : 'synced';

  return {
    syncState,
    activePatient: state.activePatient,
    pendingPatient: state.pendingPatient,
    finishLoading,
    applyPendingPatient,
    stopSync,
    startSync,
    reportManualSwitch,
    reportLocation,
  };
}
